/**
 * Opening the create dialog for a new outline child. Split out of the row renderer so the same
 * decision — which line owns the child, and what the child's parent is — is reachable from a
 * keyboard shortcut on the relation, which has no cell renderer and no grid node to hand.
 *
 * Deliberately free of grid and renderer types: the input is a row and the entity pair, nothing else.
 */
import { Entity, EntityLink } from '@jms/model';
import { OutlineEntityKinds, OutlineRow } from './outline.authoring.model';
import { isOutlineServiceLine, isOutlineSubtask } from './outline.selection';

/** What a new child hangs off: its owning line, and the record it nests under. */
export interface OutlineCreateParent {
  serviceLine: Entity | undefined;
  parent: EntityLink | undefined;
}

export interface OutlineChildCreateRequest {
  dialogs: { open(request: unknown): unknown };
  kinds: OutlineEntityKinds | null | undefined;
  row: OutlineRow;
  readonly?: boolean;
  afterSave?: (result: unknown) => void | Promise<void>;
}

/**
 * Resolves the owning line and the nesting parent for a child of `row`, or null when the row cannot
 * take one. The outline is two levels deep below a line: a line takes tasks, a root task takes
 * subtasks, and a subtask takes nothing.
 */
export function resolveOutlineCreateParent(row: OutlineRow, kinds: OutlineEntityKinds | null | undefined): OutlineCreateParent | null {

  if (!kinds || isOutlineSubtask(row, kinds)) return null;
  const serviceLine = kinds.lineRef(row);
  const parent = isOutlineServiceLine(row, kinds)
    ? serviceLine ? { ...serviceLine, type: kinds.line } : undefined
    : { _id: row.sourceId, name: row.name, type: kinds.taskParent ?? kinds.task };
  return { serviceLine, parent };
}

/** No-op when readonly, when the entity pair is unknown, or when the row is a subtask. */
export function openOutlineChildCreate(request: OutlineChildCreateRequest): void {

  const { dialogs, kinds, row } = request;
  if (request.readonly === true || !kinds) return;
  const target = resolveOutlineCreateParent(row, kinds);
  if (!target) return;
  void dialogs.open({
    entity: kinds.task,
    operation: 'create',
    params: { [kinds.lineParam]: target.serviceLine, readonly: false, ...kinds.createParams },
    initialValues: { parent: target.parent },
    source: { entity: kinds.outline },
    afterSave: request.afterSave,
  });
}
